import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PEDashboardTab } from '@/types/pe.types';
import {
  PETotalsTab,
  PEMarketsTab,
  PERatesTab,
  PEFairPriceTab,
  PEFeedstocksTab
} from '@/components/pe';
import FeedstocksTab from '@/components/polymer/FeedstocksTab';
import UsitcChart from '@/components/polymer/UsitcChart';
import FreeTrialFade from '@/components/common/FreeTrialFade';
import InventoryChangeView from '@/components/polymer/InventoryChangeView';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';
import DemandProductionCapacityChart from '@/components/charts/DemandProductionCapacityChart';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { getPEInventoryMetrics, getPEProductionCapacity } from '@/services/marketIntelService';
import { format } from 'date-fns';
import { useTheme } from '@/contexts/ThemeContext';

const DEFAULT_DATE_FROM = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().split('T')[0];
})();

const DEFAULT_DATE_TO = new Date().toISOString().split('T')[0];

const PE_CONFIG: PolymerDashboardConfig = {
  polymer: 'PE',
  polymerFullName: 'Polyethylene',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: PEDashboardTab.FAIR_PRICE, label: 'Fair Resin Price' },
    { key: PEDashboardTab.PE_TOTALS, label: 'Totals' },
    { key: PEDashboardTab.INVENTORY_CHANGE, label: 'Inventory Change' },
    { key: PEDashboardTab.DEMAND_PRODUCTION_CAPACITY, label: 'Demand/Production/Capacity' },
    { key: PEDashboardTab.FEEDSTOCKS, label: 'Feedstocks' },
    { key: PEDashboardTab.USITC, label: 'USITC' },
    { key: PEDashboardTab.MARKETS, label: 'Markets' },
    { key: PEDashboardTab.RATES, label: 'Rates' },
  ],
};

const ChartPanel: React.FC<{ title: string; children: React.ReactNode; className?: string; fullGrey?: boolean }> = ({ title, children, className, fullGrey }) => {
  const { isDark } = useTheme();
  const [isSmallTitle, setIsSmallTitle] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallTitle(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const bg = fullGrey && !isDark ? 'bg-rs-off-black' : 'bg-rs-off-black/60';
  return (
    <div className={`${bg} backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
      <h3 className={`text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 ${isSmallTitle ? 'text-center' : ''}`}>{title}</h3>
      {children}
    </div>
  );
};

const PEDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [peMetrics, setPeMetrics] = useState<any[]>([]);
  const [peCapacity, setPeCapacity] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedResinType, setSelectedResinType] = useState<'pe' | 'hdpe' | 'ldpe' | 'lldpe'>('pe');
  const [inventoryChangeMode, setInventoryChangeMode] = useState<'total' | 'cumulative'>('total');
  // Below 750px, show 1 resin type button at a time with chevrons for PE inventory change.
  const [isSmallResinSelector, setIsSmallResinSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [resinSelectIndex, setResinSelectIndex] = useState(0);
  // Below 750px, show 1 resin type button at a time with chevrons for Demand/Production/Capacity.
  const [isSmallDpcSelector, setIsSmallDpcSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [dpcSelectIndex, setDpcSelectIndex] = useState(0);
  // Below 1300px, hide data labels in Demand/Production/Capacity chart.
  const [hideChartLabels, setHideChartLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1299px)').matches,
  );
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);

    const resinTypes = ['pe', 'hdpe', 'ldpe', 'lldpe'] as const;

    Promise.all([
      Promise.all(resinTypes.map(type => getPEInventoryMetrics(type))),
      getPEProductionCapacity(),
    ])
      .then(([metricsArrays, capacity]) => {
        const combinedMetrics = metricsArrays.flat();
        setPeMetrics(combinedMetrics);
        setPeCapacity(capacity);
      })
      .catch(err => console.error('Failed to fetch PE data:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const smallMq = window.matchMedia('(max-width: 749px)');
    const chartLabelsMq = window.matchMedia('(max-width: 1299px)');
    const overviewMq = window.matchMedia('(max-width: 750px)');
    const largeDpcHeightMq = window.matchMedia('(min-width: 1024px) and (max-width: 1105px)');
    const smallHandler = (e: MediaQueryListEvent) => {
      setIsSmallResinSelector(e.matches);
      setResinSelectIndex(0);
      setIsSmallDpcSelector(e.matches);
      setDpcSelectIndex(0);
    };
    const chartLabelsHandler = (e: MediaQueryListEvent) => setHideChartLabels(e.matches);
    const overviewHandler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    const largeDpcHeightHandler = (e: MediaQueryListEvent) => setIsLargeDpcHeight(e.matches);
    smallMq.addEventListener('change', smallHandler);
    chartLabelsMq.addEventListener('change', chartLabelsHandler);
    overviewMq.addEventListener('change', overviewHandler);
    largeDpcHeightMq.addEventListener('change', largeDpcHeightHandler);
    return () => {
      smallMq.removeEventListener('change', smallHandler);
      chartLabelsMq.removeEventListener('change', chartLabelsHandler);
      overviewMq.removeEventListener('change', overviewHandler);
      largeDpcHeightMq.removeEventListener('change', largeDpcHeightHandler);
    };
  }, []);

  const monthlyMetrics = keepFirstEntryPerMonth(peMetrics).sort((a, b) =>
    (a.date || '') < (b.date || '') ? -1 : 1
  );

  const monthlyCapacity = keepFirstEntryPerMonth(peCapacity.filter(d => !d.resin_type || d.resin_type === 'pe')).sort((a, b) =>
    (a.date || '') < (b.date || '') ? -1 : 1
  );

  const monthlyLabels = monthlyMetrics.map((d) => {
    try {
      return format(new Date((d.date || '') + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return d.date || '';
    }
  });

  const renderTabContent = (activeTab: string) => {
    if (activeTab === 'overview') {
      const filteredMetricsOverview = peMetrics.filter((d) => !d.resin_type || d.resin_type === selectedResinType);
      const monthlyMetricsOverview = keepFirstEntryPerMonth(filteredMetricsOverview).sort((a, b) =>
        (a.date || '') < (b.date || '') ? -1 : 1
      );
      const monthlyLabelsOverview = monthlyMetricsOverview.map((d) => {
        try {
          return format(new Date((d.date || '') + 'T00:00:00'), 'MMM yyyy');
        } catch {
          return d.date || '';
        }
      });

      return (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {isSmallOverview ? (
              <PEFairPriceTab
                dateFrom={DEFAULT_DATE_FROM}
                dateTo={DEFAULT_DATE_TO}
                labelMode="overview"
                fullBleed
              />
            ) : (
              <ChartPanel title="Fair Resin Price" className="flex flex-col">
                <div className="flex-1 flex flex-col min-h-0">
                  <PEFairPriceTab
                    dateFrom={DEFAULT_DATE_FROM}
                    dateTo={DEFAULT_DATE_TO}
                    labelMode="overview"
                    showBorder
                  />
                </div>
              </ChartPanel>
            )}

            {isSmallOverview ? (
              <div className="-mx-6">
                <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                  Demand, Production & Capacity
                </h3>
                <div className="px-2">
                  <DemandProductionCapacityChart
                    production={monthlyMetricsOverview.map((d) => d.domestic_production ?? null)}
                    demand={monthlyMetricsOverview.map((d) => d.total_sales ?? null)}
                    capacity={monthlyMetricsOverview.map((d) => d.capacity ?? null)}
                    labels={monthlyLabelsOverview}
                    polymer="PE"
                    showDataLabels={false}
                    className="w-full h-[400px] flex flex-col"
                    selectedResinType={selectedResinType}
                    onResinTypeChange={setSelectedResinType}
                    isOverviewPage={true}
                  />
                </div>
              </div>
            ) : (
              <ChartPanel title="Demand, Production & Capacity" className="flex flex-col" fullGrey>
                <DemandProductionCapacityChart
                  production={monthlyMetricsOverview.map((d) => d.domestic_production ?? null)}
                  demand={monthlyMetricsOverview.map((d) => d.total_sales ?? null)}
                  capacity={monthlyMetricsOverview.map((d) => d.capacity ?? null)}
                  labels={monthlyLabelsOverview}
                  polymer="PE"
                  showDataLabels={false}
                  showBorder
                  className="w-full h-[575px] flex flex-col"
                  selectedResinType={selectedResinType}
                  onResinTypeChange={setSelectedResinType}
                  isOverviewPage={true}
                />
              </ChartPanel>
            )}
          </div>

          {isSmallOverview ? (
            <div className="-mx-6">
              <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                Inventory & Production
              </h3>
              <PETotalsTab
                resinType={selectedResinType}
                isOverviewPage={true}
              />
            </div>
          ) : (
            <ChartPanel title="PE Totals — Inventory & Production">
              <PETotalsTab
                resinType={selectedResinType}
                isOverviewPage={true}
              />
            </ChartPanel>
          )}

          {isSmallOverview ? (
            <FeedstocksTab polymer="PE" fullBleed />
          ) : (
            <ChartPanel title="Feedstocks">
              <FeedstocksTab polymer="PE" />
            </ChartPanel>
          )}
        </div>
      );
    }

    switch (activeTab) {
      case PEDashboardTab.PE_TOTALS:
        return (
          <PETotalsTab fullBleed />
        );
      case PEDashboardTab.INVENTORY_CHANGE:
        const SUB_TYPES = [
          { key: 'pe', label: 'ALL PE' },
          { key: 'hdpe', label: 'HDPE' },
          { key: 'ldpe', label: 'LDPE' },
          { key: 'lldpe', label: 'LLDPE' },
        ] as const;

        const inventoryData = peMetrics
          .filter((d: any) => d.resin_type === selectedResinType)
          .sort((a: any, b: any) => (a.date || '') < (b.date || '') ? -1 : 1);
        const filteredCommentary = inventoryData
          .map((d: any, idx: number, arr: any[]) => {
            if (d.inventory_change_cumulative === null || d.inventory_change_cumulative === undefined) {
              const cumulative = arr.slice(0, idx + 1).reduce((sum, item) => {
                return sum + (item.inventory_change_monthly || 0);
              }, 0);
              return { ...d, inventory_change_cumulative: cumulative };
            }
            return d;
          });

        return (
          <div className={`flex flex-col gap-6 ${isSmallResinSelector ? 'p-0 -mx-6' : 'p-4'}`}>
            {isSmallResinSelector ? (
              // Below 750px: centered chevron selector + mode buttons
              <div className="flex flex-col gap-3 items-center">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, resinSelectIndex - 1);
                      setResinSelectIndex(newIndex);
                      setSelectedResinType(SUB_TYPES[newIndex].key);
                    }}
                    disabled={resinSelectIndex === 0}
                    className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                    aria-label="Previous resin type"
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                  </button>

                  <button
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      selectedResinType === SUB_TYPES[resinSelectIndex].key
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    {SUB_TYPES[resinSelectIndex].label}
                  </button>

                  <button
                    onClick={() => {
                      const newIndex = Math.min(SUB_TYPES.length - 1, resinSelectIndex + 1);
                      setResinSelectIndex(newIndex);
                      setSelectedResinType(SUB_TYPES[newIndex].key);
                    }}
                    disabled={resinSelectIndex >= SUB_TYPES.length - 1}
                    className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                    aria-label="Next resin type"
                  >
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </button>
                </div>

                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setInventoryChangeMode('total')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      inventoryChangeMode === 'total'
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    Total Change
                  </button>
                  <button
                    onClick={() => setInventoryChangeMode('cumulative')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      inventoryChangeMode === 'cumulative'
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    Cumulative
                  </button>
                </div>
              </div>
            ) : (
              // Above 750px: original horizontal layout with resin buttons on left, mode buttons on right
              <div className="flex justify-between items-center gap-4">
                <div className="flex gap-2 flex-wrap">
                  {SUB_TYPES.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setSelectedResinType(key)}
                      className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                        selectedResinType === key
                          ? 'bg-rs-green text-black'
                          : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setInventoryChangeMode('total')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      inventoryChangeMode === 'total'
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    Total Change
                  </button>
                  <button
                    onClick={() => setInventoryChangeMode('cumulative')}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      inventoryChangeMode === 'cumulative'
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    Cumulative
                  </button>
                </div>
              </div>
            )}
            <InventoryChangeView
              data={filteredCommentary}
              inventoryChangeKey="inventory_change_monthly"
              inventoryChangeCumulativeKey="inventory_change_cumulative"
              mode={inventoryChangeMode}
              onModeChange={setInventoryChangeMode}
              polymer="PE"
            />
          </div>
        );
      case PEDashboardTab.DEMAND_PRODUCTION_CAPACITY:
        const SUB_TYPES_DPC = [
          { key: 'pe', label: 'ALL PE' },
          { key: 'hdpe', label: 'HDPE' },
          { key: 'ldpe', label: 'LDPE' },
          { key: 'lldpe', label: 'LLDPE' },
        ] as const;

        const filteredMetricsDPC = peMetrics.filter((d) => !d.resin_type || d.resin_type === selectedResinType);
        const monthlyMetricsDPC = keepFirstEntryPerMonth(filteredMetricsDPC).sort((a, b) =>
          (a.date || '') < (b.date || '') ? -1 : 1
        );
        const monthlyLabelsDPC = monthlyMetricsDPC.map((d) => {
          try {
            return format(new Date((d.date || '') + 'T00:00:00'), 'MMM yyyy');
          } catch {
            return d.date || '';
          }
        });

        return (
          <div className={`flex flex-col gap-6 ${isSmallDpcSelector ? 'p-0 -mx-6' : 'p-4'}`}>
            {isSmallDpcSelector ? (
              // Below 750px: 1 resin button at a time with chevrons
              <div className="flex items-center gap-2 justify-center">
                <button
                  onClick={() => {
                    const newIndex = Math.max(0, dpcSelectIndex - 1);
                    setDpcSelectIndex(newIndex);
                    setSelectedResinType(SUB_TYPES_DPC[newIndex].key);
                  }}
                  disabled={dpcSelectIndex === 0}
                  className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                  aria-label="Previous resin type"
                >
                  <ChevronLeft size={28} strokeWidth={2.5} />
                </button>

                <button
                  className={`px-4 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    selectedResinType === SUB_TYPES_DPC[dpcSelectIndex].key
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  {SUB_TYPES_DPC[dpcSelectIndex].label}
                </button>

                <button
                  onClick={() => {
                    const newIndex = Math.min(SUB_TYPES_DPC.length - 1, dpcSelectIndex + 1);
                    setDpcSelectIndex(newIndex);
                    setSelectedResinType(SUB_TYPES_DPC[newIndex].key);
                  }}
                  disabled={dpcSelectIndex >= SUB_TYPES_DPC.length - 1}
                  className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                  aria-label="Next resin type"
                >
                  <ChevronRight size={28} strokeWidth={2.5} />
                </button>
              </div>
            ) : (
              // Above 750px: all buttons visible
              <div className="flex gap-2 flex-wrap">
                {SUB_TYPES_DPC.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedResinType(key)}
                    className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                      selectedResinType === key
                        ? 'bg-rs-green text-black'
                        : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className={`border border-rs-jet-light rounded-lg ${isSmallDpcSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallDpcSelector ? 'px-2 text-center' : ''}`}>Demand, Production & Capacity</h3>
              <div className="h-[700px]">
                <DemandProductionCapacityChart
                  production={monthlyMetricsDPC.map((d) => d.domestic_production ?? null)}
                  demand={monthlyMetricsDPC.map((d) => d.total_sales ?? null)}
                  capacity={monthlyMetricsDPC.map((d) => d.capacity ?? null)}
                  labels={monthlyLabelsDPC}
                  polymer="PE"
                  showDataLabels={!hideChartLabels}
                  isOverviewPage={false}
                />
              </div>
            </div>
          </div>
        );
      case PEDashboardTab.FEEDSTOCKS:
        return <FeedstocksTab polymer="PE" fullBleed />;
      case PEDashboardTab.USITC:
        return <UsitcChart polymer="PE" />;
      case PEDashboardTab.MARKETS:
        return <PEMarketsTab />;
      case PEDashboardTab.RATES:
        return <PERatesTab />;
      case PEDashboardTab.FAIR_PRICE:
        return <PEFairPriceTab dateFrom={DEFAULT_DATE_FROM} dateTo={DEFAULT_DATE_TO} labelMode="tab" fullBleed />;
      default:
        return null;
    }
  };

  const renderContent = (activeTab: string) => {
    const blurredTabs: string[] = [
      PEDashboardTab.PE_TOTALS,
      PEDashboardTab.INVENTORY_CHANGE,
      PEDashboardTab.DEMAND_PRODUCTION_CAPACITY,
      PEDashboardTab.MARKETS,
      PEDashboardTab.RATES,
    ];
    return (
      <FreeTrialFade enabled={blurredTabs.includes(activeTab)}>
        {renderTabContent(activeTab)}
      </FreeTrialFade>
    );
  };

  return (
    <PolymerDashboardLayout
      config={PE_CONFIG}
      renderContent={renderContent}
    />
  );
};

export default PEDashboard;
